import React, { useState } from 'react';
import { Search, AlertCircle, CheckCircle, X, Plus } from 'lucide-react';
import { useSelector } from 'react-redux';
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function MergeIndent() {
  const [parentIndentId, setParentIndentId] = useState('');
  const [childIndentId, setChildIndentId] = useState('');
  const [parentIndentDetails, setParentIndentDetails] = useState(null);
  const [childIndents, setChildIndents] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const roleid = useSelector((state) => state.auth.id);

  const handleParentIndentSearch = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/adminpage/indent/${parentIndentId}`);
      const data = await response.json();
      
      if (response.ok) {
        // Fetch parent indent items to display details
        const itemsResponse = await fetch(`${API_BASE_URL}/adminpage/indent/items/${parentIndentId}`);
        const itemsData = await itemsResponse.json();

        setParentIndentDetails({
          ...data,
          items: itemsData
        });
        setError('');
      } else {
        setError(data.error || 'Parent Indent not found');
        setParentIndentDetails(null);
      }
    } catch (err) {
      setError('Failed to fetch parent indent details');
      setParentIndentDetails(null);
    }
  };

  const handleChildIndentSearch = async () => {
    // Prevent adding duplicate or parent indent
    if (childIndents.some(indent => indent.IndentID === childIndentId) || 
        childIndentId === parentIndentId) {
      setError('Cannot add duplicate or parent indent');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/adminpage/indent/${childIndentId}`);
      const data = await response.json();
      
      if (response.ok) {
        // Fetch child indent items
        const itemsResponse = await fetch(`${API_BASE_URL}/adminpage/indent/items/${childIndentId}`);
        const itemsData = await itemsResponse.json();

        setChildIndents(prev => [...prev, {
          ...data, 
          items: itemsData
        }]);
        setChildIndentId('');
        setError('');
      } else {
        setError(data.error || 'Child Indent not found');
      }
    } catch (err) {
      setError('Failed to fetch child indent details');
    }
  };

  const removeChildIndent = (indentId) => {
    setChildIndents(prev => prev.filter(indent => indent.IndentID !== indentId));
  };

  const getIndentStage = (indent) => {
    if (indent.L1 === 1) return 'L2';
    if (indent.L1A === 1) return 'L1';
    return 'PE';
  };

  const canMergeStages = (parentStage, childStage) => {
    // PE can merge with any stage
    if (parentStage === 'PE') return true;
    // L1 can merge with L1 or L2
    if (parentStage === 'L1' && (childStage === 'L1' || childStage === 'L2')) return true;
    // L2 can only merge with L2
    if (parentStage === 'L2' && childStage === 'L2') return true;
    return false;
  };

  const handleMergeIndents = async () => {
    if (!parentIndentDetails || childIndents.length === 0) {
      setError('Please select a parent indent and at least one child indent');
      return;
    }

    const parentStage = getIndentStage(parentIndentDetails);
    for (const childIndent of childIndents) {
      const childStage = getIndentStage(childIndent);
      if (!canMergeStages(parentStage, childStage)) {
        setError(`Cannot merge indents. Parent indent is at ${parentStage} stage while child indent ${childIndent.IndentID} is at ${childStage} stage. Parent indent must be at an equal or lower stage.`);
        return;
      }
    }

    try {
      const response = await fetch(`${API_BASE_URL}/adminpage/indent/merge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parentIndentId: parentIndentId,
          childIndentIds: childIndents.map(indent => indent.IndentID),
          mergedBy: roleid
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess('Indents merged successfully');
        // Reset state
        setParentIndentId('');
        setChildIndentId('');
        setParentIndentDetails(null);
        setChildIndents([]);
        setError('');
      } else {
        // Handle specific error messages from the backend
        if (result.error) {
          setError(result.error);
        } else {
          setError('Failed to merge indents');
        }
      }
    } catch (err) {
      setError('Error merging indents');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold mb-6">Merge Indents</h2>
            
            {/* Parent Indent Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parent Indent
              </label>
              <div className="flex gap-4">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    value={parentIndentId}
                    onChange={(e) => setParentIndentId(e.target.value)}
                    placeholder="Enter Parent Indent ID"
                    className="w-full p-2 pr-10 border rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  <Search className="absolute right-3 top-2.5 text-gray-400" size={20} />
                </div>
                <button
                  onClick={handleParentIndentSearch}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Search size={20} />
                  Search
                </button>
              </div>
            </div>

            {/* Child Indent Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Child Indent(s)
              </label>
              <div className="flex gap-4 mb-4">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    value={childIndentId}
                    onChange={(e) => setChildIndentId(e.target.value)}
                    placeholder="Enter Child Indent ID"
                    className="w-full p-2 pr-10 border rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  <Search className="absolute right-3 top-2.5 text-gray-400" size={20} />
                </div>
                <button
                  onClick={handleChildIndentSearch}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Plus size={20} />
                  Add Child
                </button>
              </div>

              {/* Added Child Indents */}
              {childIndents.length > 0 && (
                <div className="space-y-2">
                  {childIndents.map((indent) => (
                    <div 
                      key={indent.IndentID} 
                      className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                    >
                      <span>
                        Indent {indent.IndentID} - {indent.ProjectName} (Created by: {indent.UserName}, Stage: {getIndentStage(indent)})
                      </span>
                      <button 
                        onClick={() => removeChildIndent(indent.IndentID)}
                        className="text-red-500 hover:bg-red-50 p-1 rounded"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Error Handling */}
            {error && (
              <div className="flex items-start gap-2 p-4 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="mt-0.5 flex-shrink-0" size={20} />
                <span>{error}</span>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="flex items-start gap-2 p-4 mb-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                <CheckCircle className="mt-0.5 flex-shrink-0" size={20} />
                <span>{success}</span>
              </div>
            )}

            {/* Parent Indent Details */}
            {parentIndentDetails && (
              <div className="border rounded-lg p-4 mb-6">
                <h3 className="font-semibold mb-4">Parent Indent Details:</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-600">Indent ID:</span>
                    <span className="font-medium ml-2">{parentIndentDetails.IndentID}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Project Name:</span>
                    <span className="font-medium ml-2">{parentIndentDetails.ProjectName}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Stage:</span>
                    <span className="font-medium ml-2">{getIndentStage(parentIndentDetails)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Created By:</span>
                    <span className="font-medium ml-2">{parentIndentDetails.UserName}</span>
                  </div>
                </div>

                {/* Parent Indent Items */}
                {parentIndentDetails.items && parentIndentDetails.items.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-semibold mb-2">Current Indent Items:</h4>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border p-2 text-left">Item Description</th>
                          <th className="border p-2 text-left">Quantity</th>
                          <th className="border p-2 text-left">UOM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parentIndentDetails.items.map((item) => (
                          <tr key={item.IndentItemID}>
                            <td className="border p-2">{item.ItemDescription}</td>
                            <td className="border p-2">{item.Quantity}</td>
                            <td className="border p-2">{item.UOM}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Merge Button */}
            <div className="flex justify-end">
              <button
                onClick={handleMergeIndents}
                disabled={!parentIndentDetails || childIndents.length === 0}
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors 
                  disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Plus size={20} />
                Merge Indents
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default MergeIndent;